import React from 'react';

const ScheduleModal = ({ isOpen, onClose, schedule, onBackdropClick }) => {
    if (!isOpen || !schedule) return null;

    const formatTime = (time) => time.substring(0, 5);

    const formatDate = (date) => {
        if (!date) return '';
        const dateObj = new Date(date);
        const dayNames = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
        const months = ['tháng 1', 'tháng 2', 'tháng 3', 'tháng 4', 'tháng 5', 'tháng 6',
                       'tháng 7', 'tháng 8', 'tháng 9', 'tháng 10', 'tháng 11', 'tháng 12'];
        return `${dayNames[dateObj.getDay()]}, ${dateObj.getDate()} ${months[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'scheduled': return 'bg-blue-100 text-blue-800';
            case 'completed': return 'bg-green-100 text-green-800';
            case 'cancelled': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'scheduled': return 'Đã lên lịch';
            case 'completed': return 'Hoàn thành';
            case 'cancelled': return 'Đã hủy';
            default: return 'Không xác định';
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onBackdropClick?.();
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={handleBackdropClick}
        >
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl transform transition-all duration-300 scale-100 opacity-100 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="relative bg-gradient-to-r from-indigo-500 to-purple-600 p-6 rounded-t-2xl">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors duration-200"
                        aria-label="Đóng"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <div className="text-white">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                <span className="text-xl">📚</span>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold leading-tight">
                                    {schedule.title || schedule.className}
                                </h2>
                                {schedule.className && schedule.title && schedule.className !== schedule.title && (
                                    <p className="text-white/80 text-sm">{schedule.className}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Time and Date Info */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                                <span className="text-sm">🕒</span>
                            </div>
                            <div>
                                <p className="font-semibold text-slate-800">Thời gian</p>
                                <p className="text-sm text-slate-600">
                                    {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                                </p>
                            </div>
                        </div>

                        {schedule.date && (
                            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                    <span className="text-sm">📅</span>
                                </div>
                                <div>
                                    <p className="font-semibold text-slate-800">Ngày học</p>
                                    <p className="text-sm text-slate-600">{formatDate(schedule.date)}</p>
                                </div>
                            </div>
                        )}

                        {schedule.status && (
                            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                    <span className="text-sm">📊</span>
                                </div>
                                <div className="flex items-center justify-between w-full">
                                    <div>
                                        <p className="font-semibold text-slate-800">Trạng thái</p>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(schedule.status)}`}>
                                        {getStatusText(schedule.status)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    {schedule.description && (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <span className="text-lg">📝</span>
                                <h3 className="font-semibold text-slate-800">Mô tả</h3>
                            </div>
                            <div className="bg-gradient-to-br from-slate-50 to-indigo-50/30 rounded-xl p-4 border border-slate-100">
                                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                                    {schedule.description}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* No description fallback */}
                    {!schedule.description && (
                        <div className="text-center py-6">
                            <div className="text-4xl mb-3">📋</div>
                            <p className="text-slate-500 text-sm">Không có mô tả chi tiết cho buổi học này</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 pb-6">
                    <button
                        onClick={onClose}
                        className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                        Đóng
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ScheduleModal;